import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Copy, KeyRound, UserPlus } from 'lucide-react';
import { Student, UploadApi } from '@/services/school.api';
import { StudentsApi } from '@/services/school.api';
import { AcademicClassesApi, AcademicYearsApi } from '@/services/academic.api';
import { CustomFieldsApi } from '@/services/custom-fields.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Modal } from '@/components/ui/Modal';
import { Field, Input, Select, Textarea } from '@/components/ui/Input';
import { useTerminology } from '@/hooks/useTerminology';
import { usePermissions } from '@/hooks/usePermissions';

const GENDERS = ['male', 'female', 'other'] as const;
const GUARDIAN_GENDERS = ['male', 'female'] as const;
const STUDENT_STATUSES = ['active', 'inactive'] as const;

const schema = z.object({
  firstName: z.string().min(1, 'Required').max(128),
  lastName: z.string().optional().or(z.literal('')),
  dob: z.string().min(1, 'Required'),
  gender: z.enum(GENDERS),
  mobile: z.string().optional().or(z.literal('')),
  currentAddress: z.string().optional().or(z.literal('')),
  permanentAddress: z.string().optional().or(z.literal('')),
  academicYearId: z.string().min(1, 'Required'),
  classId: z.string().min(1, 'Required'),
  sectionId: z.string().min(1, 'Required'),
  admissionDate: z.string().min(1, 'Required'),
  admissionNo: z.string().optional().or(z.literal('')),
  rollNumber: z.string().optional().or(z.literal('')),
  status: z.enum(STUDENT_STATUSES).optional(),
  // Guardian — required on create, hidden entirely on edit.
  guardianFirstName: z.string().optional().or(z.literal('')),
  guardianLastName: z.string().optional().or(z.literal('')),
  guardianEmail: z.string().optional().or(z.literal('')),
  guardianMobile: z.string().optional().or(z.literal('')),
  guardianGender: z.enum(GUARDIAN_GENDERS).optional(),
  guardianUsername: z.string().optional().or(z.literal('')),
});

type FormValues = z.infer<typeof schema>;

export function StudentRegistrationPage() {
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const term = useTerminology();
  const { can } = usePermissions();
  const canSave = can('/students', 'create');
  const today = new Date().toISOString().slice(0, 10);

  const [credentialsModal, setCredentialsModal] = useState<Student['credentials'] | null>(null);

  const { data: yearsPage, isSuccess: yearsLoaded } = useQuery({
    queryKey: ['academic-years', 'all-for-selector'],
    queryFn: () => AcademicYearsApi.list({ limit: 100, sortBy: 'start_date', sortOrder: 'desc' }),
  });
  const years = yearsPage?.items ?? [];

  const { data: classesPage, isSuccess: classesLoaded } = useQuery({
    queryKey: ['academic-classes', 'all-for-selector'],
    queryFn: () => AcademicClassesApi.list({ limit: 100 }),
  });
  const classes = classesPage?.items ?? [];

  const { data: student } = useQuery({
    queryKey: ['student', id],
    queryFn: () => StudentsApi.get(id!),
    enabled: isEdit,
  });

  const { data: customFields } = useQuery({
    queryKey: ['custom-fields', 'active'],
    queryFn: CustomFieldsApi.listActive,
  });

  const defaultYearId = useMemo(
    () => years.find((y) => y.isCurrent)?.id ?? years[0]?.id ?? '',
    [years],
  );

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      firstName: '',
      lastName: '',
      dob: '',
      gender: 'male',
      mobile: '',
      currentAddress: '',
      permanentAddress: '',
      academicYearId: '',
      classId: '',
      sectionId: '',
      admissionDate: today,
      admissionNo: '',
      rollNumber: '',
      status: 'active',
      guardianFirstName: '',
      guardianLastName: '',
      guardianEmail: '',
      guardianMobile: '',
      guardianGender: 'male',
      guardianUsername: '',
    },
  });

  const watchedYear = watch('academicYearId');
  const watchedClass = watch('classId');
  const sections = classes.find((c) => c.id === watchedClass)?.sections ?? [];

  // Auto-suggest the admission number (create-mode only) whenever the year changes.
  const { data: suggested } = useQuery({
    queryKey: ['next-admission-number', watchedYear],
    queryFn: () => StudentsApi.nextAdmissionNumber(watchedYear || undefined),
    enabled: !isEdit && !!watchedYear,
  });
  useEffect(() => {
    if (!isEdit && suggested && !watch('admissionNo')) {
      setValue('admissionNo', suggested.admissionNo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggested, isEdit]);

  // Prefill once for edit mode. Waits for the years/classes option lists to be
  // loaded first — a native <select>'s value can only "stick" to an <option>
  // that already exists in the DOM; setting it before the options query
  // resolves silently fails, and the browser never retries it once the
  // matching <option> is added later.
  const [seeded, setSeeded] = useState(false);
  useEffect(() => {
    if (!isEdit || !student || !yearsLoaded || !classesLoaded || seeded) return;
    reset((prev) => ({
      ...prev,
      firstName: student.firstName ?? '',
      lastName: student.name && student.firstName ? student.name.replace(student.firstName, '').trim() : '',
      dob: student.dob?.slice(0, 10) ?? '',
      gender: (student.gender as (typeof GENDERS)[number]) ?? 'male',
      mobile: student.mobile ?? '',
      currentAddress: student.currentAddress ?? '',
      permanentAddress: student.permanentAddress ?? '',
      academicYearId: student.academicYearId ?? '',
      classId: student.classId ?? '',
      sectionId: student.sectionId ?? '',
      admissionDate: student.admissionDate?.slice(0, 10) ?? today,
      admissionNo: student.admissionNo ?? '',
      rollNumber: student.rollNumber != null ? String(student.rollNumber) : '',
    }));
    const extra: Record<string, string | string[]> = {};
    for (const v of student.extraFieldValues) {
      if (v.value != null) extra[v.fieldId] = v.value;
    }
    setExtraValues(extra);
    setSeeded(true);
  }, [isEdit, student, yearsLoaded, classesLoaded, seeded, reset, today]);

  useEffect(() => {
    if (!isEdit) setValue('academicYearId', defaultYearId || watch('academicYearId'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultYearId, isEdit]);

  const [photoUploading, setPhotoUploading] = useState(false);
  const [studentImage, setStudentImage] = useState<string | null>(null);
  const uploadPhoto = async (file: File) => {
    setPhotoUploading(true);
    try {
      const { url } = await UploadApi.upload(file);
      setStudentImage(url);
    } finally {
      setPhotoUploading(false);
    }
  };

  const [guardianPhotoUploading, setGuardianPhotoUploading] = useState(false);
  const [guardianImage, setGuardianImage] = useState<string | null>(null);
  const uploadGuardianPhoto = async (file: File) => {
    setGuardianPhotoUploading(true);
    try {
      const { url } = await UploadApi.upload(file);
      setGuardianImage(url);
    } finally {
      setGuardianPhotoUploading(false);
    }
  };

  const [extraValues, setExtraValues] = useState<Record<string, string | string[]>>({});
  const [extraFileUploading, setExtraFileUploading] = useState<Record<string, boolean>>({});
  const setExtraValue = (fieldId: string, value: string | string[]) =>
    setExtraValues((prev) => ({ ...prev, [fieldId]: value }));
  const uploadExtraFile = async (fieldId: string, file: File) => {
    setExtraFileUploading((prev) => ({ ...prev, [fieldId]: true }));
    try {
      const { url } = await UploadApi.upload(file);
      setExtraValue(fieldId, url);
    } finally {
      setExtraFileUploading((prev) => ({ ...prev, [fieldId]: false }));
    }
  };
  const extraFieldsPayload = () =>
    (customFields ?? []).map((f) => ({ fieldId: f.id, value: extraValues[f.id] ?? (f.type === 'checkbox' ? [] : '') }));

  const save = useMutation({
    mutationFn: async (v: FormValues) => {
      if (!isEdit) {
        return StudentsApi.create({
          firstName: v.firstName,
          lastName: v.lastName || undefined,
          dob: v.dob,
          gender: v.gender,
          mobile: v.mobile || undefined,
          image: studentImage || undefined,
          currentAddress: v.currentAddress || undefined,
          permanentAddress: v.permanentAddress || undefined,
          sectionId: v.sectionId,
          academicYearId: v.academicYearId,
          admissionDate: v.admissionDate,
          admissionNo: v.admissionNo || undefined,
          rollNumber: v.rollNumber ? Number(v.rollNumber) : undefined,
          status: v.status,
          extraFields: extraFieldsPayload(),
          guardian: {
            firstName: v.guardianFirstName || '',
            lastName: v.guardianLastName || undefined,
            email: v.guardianEmail || undefined,
            mobile: v.guardianMobile || '',
            gender: v.guardianGender ?? 'male',
            username: v.guardianUsername || undefined,
            image: guardianImage || undefined,
          },
        });
      }
      return StudentsApi.update(id!, {
        firstName: v.firstName,
        lastName: v.lastName || undefined,
        dob: v.dob,
        gender: v.gender,
        mobile: v.mobile || undefined,
        image: studentImage || undefined,
        currentAddress: v.currentAddress || undefined,
        permanentAddress: v.permanentAddress || undefined,
        rollNumber: v.rollNumber ? Number(v.rollNumber) : undefined,
        extraFields: extraFieldsPayload(),
        // Only send the placement pair when the section actually changed from
        // what was loaded — sending it unconditionally would create a
        // "no-op" new enrollment every time the form is saved.
        ...(v.sectionId && v.sectionId !== student?.sectionId
          ? { sectionId: v.sectionId, academicYearId: v.academicYearId }
          : {}),
      });
    },
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ['students'] });
      qc.invalidateQueries({ queryKey: ['student', id] });
      qc.invalidateQueries({ queryKey: ['school-stats'] });
      if (!isEdit && result.credentials) {
        setCredentialsModal(result.credentials);
      } else {
        navigate('/students');
      }
    },
  });

  return (
    <>
      <button
        onClick={() => navigate('/students')}
        className="mb-3 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800"
      >
        <ArrowLeft className="h-4 w-4" /> Back to students
      </button>

      <PageHeader
        title={isEdit ? 'Edit student' : 'New student admission'}
        description={
          isEdit
            ? 'Update the student profile, or move them to a different class/section.'
            : `Capture the student's details, class placement and guardian in one place.`
        }
      />

      <form onSubmit={handleSubmit((v) => save.mutate(v))} className="space-y-6 pb-24">
        <section className="card p-5">
          <h3 className="mb-4 font-semibold text-slate-900">Student details</h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Field label="First name" required error={errors.firstName?.message}>
              <Input {...register('firstName')} />
            </Field>
            <Field label="Last name">
              <Input {...register('lastName')} />
            </Field>
            <Field label="Date of birth" required error={errors.dob?.message}>
              <Input type="date" {...register('dob')} />
            </Field>

            <Field label="Gender" required error={errors.gender?.message}>
              <Select {...register('gender')}>
                {GENDERS.map((g) => (
                  <option key={g} value={g} className="capitalize">
                    {g}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Mobile">
              <Input {...register('mobile')} placeholder="9876543210" />
            </Field>
            <Field label="Photo">
              <div className="flex items-center gap-2">
                <input
                  type="file"
                  accept="image/*"
                  disabled={photoUploading}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) uploadPhoto(f);
                    e.target.value = '';
                  }}
                />
                {photoUploading && <span className="text-xs text-slate-400">Uploading…</span>}
                {studentImage && !photoUploading && <span className="text-xs text-green-600">Uploaded</span>}
              </div>
            </Field>

            <Field label="Current address" className="sm:col-span-3">
              <Textarea rows={2} {...register('currentAddress')} />
            </Field>
            <Field label="Permanent address" className="sm:col-span-3">
              <Textarea rows={2} {...register('permanentAddress')} />
            </Field>
          </div>
        </section>

        <section className="card p-5">
          <h3 className="mb-4 font-semibold text-slate-900">Admission &amp; placement</h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
            <Field label="Academic year" required error={errors.academicYearId?.message}>
              <Select
                {...register('academicYearId')}
                disabled={isEdit}
                onChange={(e) => {
                  setValue('academicYearId', e.target.value);
                  setValue('classId', '');
                  setValue('sectionId', '');
                }}
              >
                <option value="">— Select —</option>
                {years.map((y) => (
                  <option key={y.id} value={y.id}>
                    {y.name}
                    {y.isCurrent ? ' (current)' : ''}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={term.level} required error={errors.classId?.message}>
              <Select
                {...register('classId')}
                disabled={!watchedYear}
                onChange={(e) => {
                  setValue('classId', e.target.value);
                  setValue('sectionId', '');
                }}
              >
                <option value="">— Select —</option>
                {classes.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={term.group} required error={errors.sectionId?.message}>
              <Select {...register('sectionId')} disabled={!watchedClass}>
                <option value="">— Select —</option>
                {sections.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Roll number" hint="Auto-numbered if left blank">
              <Input type="number" {...register('rollNumber')} placeholder="Auto" />
            </Field>

            <Field label="Admission date" required error={errors.admissionDate?.message}>
              <Input type="date" {...register('admissionDate')} />
            </Field>
            <Field
              label="Admission #"
              hint={isEdit ? undefined : 'Auto-suggested — editable if needed'}
              className="sm:col-span-2"
            >
              <Input {...register('admissionNo')} disabled={isEdit} placeholder="Auto-suggested" />
            </Field>
            {!isEdit && (
              <Field label="Status" hint="Inactive students are excluded from reports by default">
                <Select {...register('status')}>
                  {STUDENT_STATUSES.map((s) => (
                    <option key={s} value={s} className="capitalize">
                      {s}
                    </option>
                  ))}
                </Select>
              </Field>
            )}
          </div>
          {isEdit && (
            <p className="mt-3 text-xs text-slate-500">
              Changing {term.level.toLowerCase()}/{term.group.toLowerCase()} above creates a new enrollment for this
              student and retires the current one — it does not overwrite history.
            </p>
          )}
        </section>

        {!!customFields?.length && (
          <section className="card p-5">
            <h3 className="mb-4 font-semibold text-slate-900">Other details</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              {customFields.map((f) => {
                const value = extraValues[f.id];
                if (f.type === 'textarea') {
                  return (
                    <Field key={f.id} label={f.name} required={f.isRequired} className="sm:col-span-3">
                      <Textarea
                        rows={2}
                        value={(value as string) ?? ''}
                        onChange={(e) => setExtraValue(f.id, e.target.value)}
                      />
                    </Field>
                  );
                }
                if (f.type === 'dropdown') {
                  return (
                    <Field key={f.id} label={f.name} required={f.isRequired}>
                      <Select value={(value as string) ?? ''} onChange={(e) => setExtraValue(f.id, e.target.value)}>
                        <option value="">— Select —</option>
                        {(f.options ?? []).map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </Select>
                    </Field>
                  );
                }
                if (f.type === 'radio') {
                  return (
                    <Field key={f.id} label={f.name} required={f.isRequired}>
                      <div className="flex flex-wrap gap-3 pt-1.5">
                        {(f.options ?? []).map((opt) => (
                          <label key={opt} className="flex items-center gap-1.5 text-sm text-slate-700">
                            <input
                              type="radio"
                              name={`extra-${f.id}`}
                              checked={value === opt}
                              onChange={() => setExtraValue(f.id, opt)}
                            />
                            {opt}
                          </label>
                        ))}
                      </div>
                    </Field>
                  );
                }
                if (f.type === 'checkbox') {
                  const selected = Array.isArray(value) ? value : [];
                  return (
                    <Field key={f.id} label={f.name} required={f.isRequired}>
                      <div className="flex flex-wrap gap-3 pt-1.5">
                        {(f.options ?? []).map((opt) => (
                          <label key={opt} className="flex items-center gap-1.5 text-sm text-slate-700">
                            <input
                              type="checkbox"
                              checked={selected.includes(opt)}
                              onChange={(e) =>
                                setExtraValue(
                                  f.id,
                                  e.target.checked ? [...selected, opt] : selected.filter((o) => o !== opt),
                                )
                              }
                            />
                            {opt}
                          </label>
                        ))}
                      </div>
                    </Field>
                  );
                }
                if (f.type === 'file') {
                  const uploading = extraFileUploading[f.id];
                  return (
                    <Field key={f.id} label={f.name} required={f.isRequired}>
                      <div className="flex items-center gap-2">
                        <input
                          type="file"
                          disabled={uploading}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) uploadExtraFile(f.id, file);
                            e.target.value = '';
                          }}
                        />
                        {uploading && <span className="text-xs text-slate-400">Uploading…</span>}
                        {value && !uploading && <span className="text-xs text-green-600">Uploaded</span>}
                      </div>
                    </Field>
                  );
                }
                return (
                  <Field key={f.id} label={f.name} required={f.isRequired}>
                    <Input
                      type={f.type === 'number' ? 'number' : 'text'}
                      value={(value as string) ?? ''}
                      onChange={(e) => setExtraValue(f.id, e.target.value)}
                    />
                  </Field>
                );
              })}
            </div>
          </section>
        )}

        {!isEdit && (
          <section className="card p-5">
            <h3 className="font-semibold text-slate-900">Guardian</h3>
            <p className="mb-4 text-xs text-slate-500">
              An existing guardian with this email is reused (their details are refreshed from what you enter here);
              otherwise a new guardian account is created.
            </p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <Field label="First name" required error={errors.guardianFirstName?.message}>
                <Input {...register('guardianFirstName')} />
              </Field>
              <Field label="Last name">
                <Input {...register('guardianLastName')} />
              </Field>
              <Field label="Gender" required error={errors.guardianGender?.message}>
                <Select {...register('guardianGender')}>
                  {GUARDIAN_GENDERS.map((g) => (
                    <option key={g} value={g} className="capitalize">
                      {g}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field
                label="Email"
                required
                hint="Used to find or create the guardian"
                error={errors.guardianEmail?.message}
              >
                <Input type="email" {...register('guardianEmail')} />
              </Field>
              <Field label="Mobile" required error={errors.guardianMobile?.message}>
                <Input {...register('guardianMobile')} placeholder="9876543210" />
              </Field>
              <Field label="Login username" hint="Defaults to the email if left blank">
                <Input {...register('guardianUsername')} />
              </Field>
              <Field label="Photo">
                <div className="flex items-center gap-2">
                  <input
                    type="file"
                    accept="image/*"
                    disabled={guardianPhotoUploading}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) uploadGuardianPhoto(f);
                      e.target.value = '';
                    }}
                  />
                  {guardianPhotoUploading && <span className="text-xs text-slate-400">Uploading…</span>}
                  {guardianImage && !guardianPhotoUploading && <span className="text-xs text-green-600">Uploaded</span>}
                </div>
              </Field>
            </div>
          </section>
        )}

        {save.error && (
          <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{errMsg(save.error)}</div>
        )}

        <div className="fixed inset-x-0 bottom-0 z-10 border-t border-slate-200 bg-white/95 px-6 py-3 backdrop-blur sm:left-64">
          <div className="flex items-center justify-end gap-2">
            <button type="button" className="btn-secondary" onClick={() => navigate('/students')}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={save.isPending || !canSave}>
              <UserPlus className="mr-1.5 h-4 w-4" />
              {save.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Admit student'}
            </button>
          </div>
        </div>
      </form>

      {credentialsModal && (
        <CredentialsModal
          credentials={credentialsModal}
          onClose={() => {
            setCredentialsModal(null);
            navigate('/students');
          }}
        />
      )}
    </>
  );
}

function CredentialsModal({
  credentials,
  onClose,
}: {
  credentials: NonNullable<Student['credentials']>;
  onClose: () => void;
}) {
  const copy = (text: string) => navigator.clipboard.writeText(text);
  return (
    <Modal
      open
      onClose={onClose}
      title="Student admitted"
      description="Save these login details now — they won't be shown again."
      size="md"
      footer={
        <button className="btn-primary" onClick={onClose}>
          Done
        </button>
      }
    >
      <div className="space-y-4">
        <div className="rounded-lg border border-slate-200 p-4">
          <div className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-slate-900">
            <KeyRound className="h-4 w-4" /> Student login
          </div>
          <CredentialRow label="Username" value={credentials.studentUsername} onCopy={copy} />
          <CredentialRow label="Password" value={credentials.studentPassword} onCopy={copy} />
          {credentials.studentPasswordFallbackApplied && (
            <p className="mt-2 text-xs text-amber-700">
              No (or too short a) mobile number was given, so a fallback password was generated.
            </p>
          )}
        </div>
        {credentials.guardianCredentials && (
          <div className="rounded-lg border border-slate-200 p-4">
            <div className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-slate-900">
              <KeyRound className="h-4 w-4" /> New guardian login
            </div>
            <CredentialRow label="Username" value={credentials.guardianCredentials.username} onCopy={copy} />
            <CredentialRow label="Password" value={credentials.guardianCredentials.password} onCopy={copy} />
            {credentials.guardianCredentials.passwordFallback && (
              <p className="mt-2 text-xs text-amber-700">
                No (or too short a) mobile number was given, so a fallback password was generated.
              </p>
            )}
          </div>
        )}
        {!credentials.guardianCredentials && (
          <p className="text-sm text-slate-500">
            This student was linked to an existing guardian — no new guardian login was created.
          </p>
        )}
      </div>
    </Modal>
  );
}

function CredentialRow({
  label,
  value,
  onCopy,
}: {
  label: string;
  value: string;
  onCopy: (v: string) => void;
}) {
  return (
    <div className="flex items-center justify-between py-1">
      <span className="text-sm text-slate-500">{label}</span>
      <div className="flex items-center gap-2">
        <code className="rounded bg-slate-50 px-2 py-1 font-mono text-sm">{value}</code>
        <button
          type="button"
          className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          onClick={() => onCopy(value)}
          title="Copy"
        >
          <Copy className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

function errMsg(e: unknown): string | undefined {
  if (!e) return undefined;
  const anyE = e as any;
  return anyE?.response?.data?.error?.message ?? anyE?.message ?? 'Something went wrong';
}
